import { useState, type ReactNode } from 'react'
import type { TmsInvoiceRow } from '@numeo/types-main-web-app'
import { Badge, Button } from '@/components/ds2'
import { LoadFixActions, useFixSlots } from './LoadFixActions'
import { bolSlotOf } from './bolSlot'
import { useGeneralAgentChatStore } from '../../hooks/generalAgentChatStore'
import { documentSlotsOf, LoadRow } from './LoadRow'
import { ageLineOf, invoiceStatusOf, isPastDue } from './invoiceRows'
import { documentActions, followUpMessage } from './rowActions'

interface InvoiceRowProps {
    row: TmsInvoiceRow
    /** The card's aged threshold in days, when the answer carries one; else the row's own payment terms decide past-due. */
    thresholdDays?: number
    onSend: (message: string) => void
}

/**
 * One invoice of an Invoices list, on the same grid as a load line (`LoadRow`): the invoice number and broker on the first line; the status
 * and the age with its due date under them. The loads are the first number and "+N" (the full list is its tooltip). RC, BOL and POD are the
 * fixed slots of its one load; a missing or failed document is a button that does its fix, and a POD with findings opens its review.
 *
 * The action column holds one primary action: "Follow up" for an unpaid invoice past due, else the fix a held, denied or disputed invoice
 * needs. Further fixes wait behind "⋯". A funded or paid invoice has no action at all.
 */
export const InvoiceRow = ({ row, thresholdDays, onSend }: InvoiceRowProps) => {
    const [fixOpen, setFixOpen] = useState(false)
    const notify = useGeneralAgentChatStore((s) => s.notify)
    const status = invoiceStatusOf(row)
    const pastDue = isPastDue(row, new Date(), thresholdDays)
    const loadNumber = row.load_numbers.length === 1 ? row.load_numbers[0] : undefined
    const settled = row.status_group === 'funded_by_factor' || row.status_group === 'paid_by_broker'
    // A document fix changes nothing once the factor funded the invoice or the broker paid it: the chips stay, the buttons go.
    const fixes = loadNumber && !settled ? documentActions(row.documents) : []
    const slots = useFixSlots({ actions: fixes, loadNumber: loadNumber ?? '', loadId: row.load_id, onOpen: () => setFixOpen(true) })

    const followUp: ReactNode =
        pastDue && !settled && row.invoice_number ? (
            <Button size="sm" variant="secondary" onClick={() => onSend(followUpMessage(row.invoice_number ?? ''))}>
                Follow up
            </Button>
        ) : null
    // The fix a held, denied or disputed invoice needs leads its column; with a follow-up, or on an invoiced one, the fixes go behind "⋯".
    const held = row.status_group === 'issues'
    const action: ReactNode =
        fixes.length > 0 && loadNumber ? (
            <LoadFixActions
                loadNumber={loadNumber}
                loadId={row.load_id}
                actions={fixes}
                onSend={onSend}
                lead={followUp ?? undefined}
                firstInColumn={held}
                open={fixOpen}
                onOpenChange={setFixOpen}
            />
        ) : followUp ? (
            // The "⋯" slot is held open, so a Follow up lines up with the ones that sit beside a "⋯" in the same list.
            <span className="flex items-center justify-end gap-1.5">
                {followUp}
                <span aria-hidden className="h-8 w-8 shrink-0" />
            </span>
        ) : undefined

    const ageWords = ageLineOf(row)
    const state: ReactNode = (
        <span className="inline-flex min-w-0 items-center gap-2">
            <Badge variant="soft" type={status.type} size="md" label={status.label} />
            <span className={`truncate ${pastDue ? 'text-warning' : ''}`}>{ageWords}</span>
        </span>
    )
    const title: ReactNode = (
        <span className="flex min-w-0 items-center gap-1.5">
            <span className="shrink-0 font-mono tabular-nums">{row.invoice_number ?? '—'}</span>
            <span className="truncate text-foreground-muted">{row.broker ?? '—'}</span>
        </span>
    )

    return (
        <li className="py-1.5 first:pt-0 last:pb-0">
            <LoadRow
                number={row.load_numbers.join(', ') || '—'}
                title={title}
                titleTooltip={`${row.invoice_number ?? ''} · ${row.broker ?? ''}`}
                state={state}
                documents={documentSlotsOf(row.documents)}
                slots={{ ...slots, bol: loadNumber && !settled ? bolSlotOf({ loadNumber, loadId: row.load_id, onFiled: notify }) : undefined }}
                amount={row.amount_due_usd ?? undefined}
                action={action}
                redFlag={row.red_flagged}
            />
        </li>
    )
}
